import { styled } from '@teko/ds-desktop';
import { logger } from '@teko/log';
import { getTrackingData } from '@teko/tracking';
import { chunkRender } from '@teko/utils';
import { useRouter } from 'next/router';
import React, { useState, useCallback, useEffect } from 'react';
import { REGION_CONTENT_MAPPING } from '../../constants/index.es.js';
import { useCardVariant } from '../../contexts/CardVariantContext.es.js';
import { ProductCardSGC } from '../ProductCardSGC/ProductCardSGC.es.js';
import { ProductCard } from '../ProductCard/ProductCard.es.js';
import { useDevice } from '../../contexts/DeviceContext.es.js';
import { hasComboDiscount } from '../../helpers/combo.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const ProductGrid = ({
  products,
  productsPerRow = 5,
  regionName,
  contactLink,
  type = "grid",
  hideCtaButton,
  itemsPerPage,
  className,
  cardVariant
}) => {
  const contextVariant = useCardVariant();
  const isSgc = (cardVariant != null ? cardVariant : contextVariant) === "sgc";
  const Card = isSgc ? ProductCardSGC : ProductCard;
  const { isMobile } = useDevice();
  const router = useRouter();
  const page = router.query.page ? Number(router.query.page) : 1;
  const [renderedProducts, setRenderedProducts] = useState([]);
  const renderGrid = useCallback((item, index) => /* @__PURE__ */ React.createElement(Grid, {
    isMobile,
    isSgc,
    key: index,
    productsPerRow,
    type
  }, /* @__PURE__ */ React.createElement(Card, __spreadProps(__spreadValues({}, item), {
    tracking: regionName ? getTrackingData({
      isTrackContent: true,
      regionName,
      contentName: REGION_CONTENT_MAPPING[regionName] || item.sku,
      target: "productDetail",
      index: itemsPerPage ? index + (page - 1) * itemsPerPage : index,
      payload: hasComboDiscount(item.bestComboDiscount) ? JSON.stringify({ promotionType: "combo" }) : void 0
    }) : {},
    ctaTracking: regionName && contactLink ? getTrackingData({
      isTrackContent: true,
      regionName,
      contentName: "contactSale",
      target: contactLink,
      payload: JSON.stringify({ sku: item.sku })
    }) : {},
    type,
    addToCart: hideCtaButton ? void 0 : item.addToCart
  }))), [
    isMobile,
    productsPerRow,
    type,
    regionName,
    contactLink,
    hideCtaButton,
    itemsPerPage,
    page,
    Card,
    isSgc
  ]);
  useEffect(() => {
    const renderProducts = async () => {
      try {
        const renderedItems = await chunkRender(products, renderGrid);
        setRenderedProducts(renderedItems);
      } catch (error) {
        logger.error("Error rendering products:", error);
      }
    };
    renderProducts();
  }, [renderGrid, products]);
  return /* @__PURE__ */ React.createElement(Wrapper, {
    isMobile,
    isSgc,
    className
  }, renderedProducts.length ? renderedProducts : products.map(renderGrid));
};
const Wrapper = styled.div(({
  theme,
  isMobile,
  isSgc
}) => `
display: flex;
flex-wrap: wrap;
column-gap: ${isSgc ? "6px" : "2px"};
place-content: ${isSgc ? "flex-start" : "flex-start space-between"};
background: ${theme == null ? void 0 : theme.colors.background};
padding: 2px 0;
&::after{
  content: "";
  flex: auto;
}
${isMobile ? "padding: 5px;" : ""}
`);
const Grid = styled.div(({
  productsPerRow,
  type,
  isMobile,
  isSgc
}) => {
  const gap = isSgc ? 6 : 2;
  const cellInset = isSgc ? gap * (productsPerRow - 1) / productsPerRow : 2;
  return `
  background: white;
  margin-bottom: ${gap}px;
  ${type === "grid" ? `width: calc(${100 / productsPerRow}% - ${cellInset}px);` : "width: 100%;"}
  ${isSgc ? "border-radius: 4px; overflow: hidden;" : isMobile ? "border-radius: 8px;" : ""}
  }`;
});

export { ProductGrid };
